import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { MouseEvent, createElement, FC } from 'react';
import { Pathname } from 'history';
import { FormattedMessage } from 'react-intl';
import KaizenPlatformLogo from '../component/primitive/KaizenPlatformLogo';
import { SPACING_UNIT, SPACING_REGULAR } from '../constant/spacing';
import { subText } from '../constant/color';
import { gridLayoutContainer, gridLayoutItem } from '../styleUtility/gridLayout';

interface Props {
  error: Error;
  solveTo: string;
  onClickBackButton: (e: MouseEvent<HTMLElement>, solveTo: string) => void;
  className?: string;
  pathname: Pathname;
  onClickHomeButton: () => void;
}

const FatalErrorScreen: FC<Props> = ({ error, solveTo, onClickBackButton, className, pathname, onClickHomeButton }) => {
  const viewItemProperties =
    // Setting at 'root path' and 'other page'
    error.name === 'PageNotFound'
      ? {
          mainMessage: '404',
          subMessage: 'root.for_fatal_error.not_found',
          backButtonHandler: () => {
            onClickHomeButton();
          },
          backButtonText: 'root.for_fatal_error.home',
        }
      : pathname === '/' || pathname === '/users/:userId'
      ? {
          mainMessage: error.message,
          subMessage: `RootError`,
          backButtonHandler: () => location.reload(),
          backButtonText: 'root.for_fatal_error.reload',
        }
      : {
          mainMessage: error.message,
          subMessage: `${error.message}.tips`,
          backButtonHandler: (e: MouseEvent<HTMLElement>) => onClickBackButton(e, solveTo),
          backButtonText: 'root.for_fatal_error.back',
        };
  return (
    <Root className={className}>
      <Logo />
      <Title variant="h4" align="center" gutterBottom>
        <FormattedMessage id={viewItemProperties.mainMessage} />
      </Title>
      <Message variant="h6">
        <FormattedMessage id={viewItemProperties.subMessage} />
      </Message>
      <BackButton variant="contained" color="primary" onClick={viewItemProperties.backButtonHandler}>
        <FormattedMessage id={viewItemProperties.backButtonText} />
      </BackButton>
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['auto'],
    rows: ['auto', 'auto', 'auto', 'auto'],
    areas: [['logo'], ['title'], ['errorMessage'], ['button']],
    alignContent: 'center',
    justifyItems: 'center',
    alignItems: 'flex-start',
  }),
  {
    marginTop: `-${SPACING_REGULAR}px`,
    marginRight: 'auto',
    marginLeft: 'auto',
  },
);

const Logo = styled(KaizenPlatformLogo)(gridLayoutItem('logo'), {
  width: 305,
  marginBottom: SPACING_UNIT * 4,
});

const Title = styled(Typography)(gridLayoutItem('title'), {
  marginBottom: SPACING_UNIT * 2,
  fontWeight: 500,
  maxWidth: 720,
}) as typeof Typography;

const Message = styled(Typography)(gridLayoutItem('errorMessage'), {
  maxWidth: 720,
  color: subText,
}) as typeof Typography;

const BackButton = styled(Button)(gridLayoutItem('button'), {
  marginTop: SPACING_UNIT * 6,
  minWidth: '150px',
}) as typeof Button;

export default FatalErrorScreen;
